import React, { forwardRef, useEffect, useState, useCallback } from "react";
import {
  Autocomplete as MuiAutocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";

import { AutocompleteProps } from "./types";
import { useDebounce } from "@shared/hooks/useDebounce";

export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(
  (
    {
      valueField = "id",
      labelField = "name",
      label,
      multiple = false,
      required = false,
      size = "small",
      onChange,
      value,
      error,
      helperText,
      queryHook,
      queryHookFilters,
      getOptionLabel,
      ...rest
    },
    ref
  ) => {
    const [options, setOptions] = useState<any[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [selectedValue, setSelectedValue] = useState<any>(
      multiple ? [] : null
    );
    const [loading, setLoading] = useState(false);

    const debouncedSearch = useDebounce(inputValue, 500);

    const handleChange = (_: any, newValue: any) => {
      setSelectedValue(newValue);

      if (onChange) {
        if (multiple) {
          const selectedValues = newValue.map((item: any) => item[valueField]);
          onChange(selectedValues);
        } else {
          onChange(newValue ? newValue[valueField] : null);
        }
      }
    };

    const fetchData = useCallback(async () => {
      setLoading(true);
      try {
        const results = await queryHook({
          page: 1,
          perPage: 99,
          search: debouncedSearch,
          ids: [],
          ...queryHookFilters,
        });

        setOptions(results || []);
      } catch (error) {
        console.error("Failed to fetch options:", error);
      } finally {
        setLoading(false);
      }
    }, [queryHook, debouncedSearch, queryHookFilters]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useEffect(() => {
      if (!value) return;

      const fetchSelectedValues = async () => {
        setLoading(true);
        try {
          const results = await queryHook({
            ids: Array.isArray(value) ? value : [value],
          });
          const selected = Array.isArray(value)
            ? results.filter((option: any) =>
                value.includes(option[valueField])
              )
            : results.find((option: any) => option[valueField] === value);

          setSelectedValue(selected || (multiple ? [] : null));
        } finally {
          setLoading(false);
        }
      };

      fetchSelectedValues();
    }, [value, valueField, multiple, queryHook]);

    return (
      <MuiAutocomplete
        {...rest}
        multiple={multiple}
        size={size}
        options={options}
        loading={loading}
        value={selectedValue}
        getOptionLabel={(option) => {
          if (getOptionLabel) {
            return getOptionLabel(option);
          }
          const labelValue = option?.[labelField];
          return labelValue !== undefined && labelValue !== null
            ? labelValue.toString()
            : "";
        }}
        isOptionEqualToValue={(option, value) =>
          option?.[valueField] === value?.[valueField]
        }
        onChange={handleChange}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "input" || reason === "clear") {
            setInputValue(newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${label}${required ? " *" : ""}`}
            variant="outlined"
            error={!!error}
            helperText={helperText}
            sx={{
              "& .MuiInputBase-root": multiple
                ? {
                    // Deixa a altura dinâmica quando for múltiplo
                    minHeight: "45px",
                    alignItems: "flex-start",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    flexWrap: "wrap",
                  }
                : {
                    height: "45px",
                  },
              "& .MuiInputBase-input": {
                fontSize: 14,
                padding: "8px 12px",
              },
            }}
            inputRef={ref}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }
);

Autocomplete.displayName = "Autocomplete";
